import { useEffect, useState, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useUser } from "../context/UserContext";

function NewMessageModal({ open, onClose, onConversationCreated }) {
    const { user } = useUser();

    const [suggested, setSuggested] = useState([]);      // people I follow
    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [starting, setStarting] = useState(false);     // opening chat

    const inputRef = useRef(null);
    const searchTimer = useRef(null);

    // ── Fetch suggested (people I follow) when modal opens ────────────────────
    useEffect(() => {
        if (!open || !user) return;
        setSearch("");
        setSearchResults([]);

        const fetchSuggested = async () => {
            const { data: follows } = await supabase
                .from("follows")
                .select("following_id")
                .eq("follower_id", user.id);

            if (!follows?.length) { setSuggested([]); return; }

            const ids = follows.map(f => f.following_id);
            const { data: profiles } = await supabase
                .from("profiles")
                .select("id, username, full_name, avatar_url")
                .in("id", ids);

            setSuggested(profiles || []);
        };

        fetchSuggested();
        setTimeout(() => inputRef.current?.focus(), 100);
    }, [open, user]);

    // ── Debounced DB search as user types ─────────────────────────────────────
    useEffect(() => {
        clearTimeout(searchTimer.current);

        if (!search.trim()) {
            setSearchResults([]);
            return;
        }

        searchTimer.current = setTimeout(async () => {
            setSearchLoading(true);
            const q = search.trim();

            const { data } = await supabase
                .from("profiles")
                .select("id, username, full_name, avatar_url")
                .or(`username.ilike.%${q}%,full_name.ilike.%${q}%`)
                .neq("id", user.id)
                .limit(12);

            setSearchResults(data || []);
            setSearchLoading(false);
        }, 280);

        return () => clearTimeout(searchTimer.current);
    }, [search, user]);

    // ── Click a user → immediately find/create conversation + open chat ────────
    const handleSelectUser = async (person) => {
        if (starting) return;
        setStarting(true);

        // 1. Look for existing conversation
        const { data: existing } = await supabase
            .from("conversations")
            .select(`
                *,
                user1:profiles!conversations_user1_id_fkey(id, username, full_name, avatar_url),
                user2:profiles!conversations_user2_id_fkey(id, username, full_name, avatar_url)
            `)
            .or(
                `and(user1_id.eq.${user.id},user2_id.eq.${person.id}),` +
                `and(user1_id.eq.${person.id},user2_id.eq.${user.id})`
            )
            .maybeSingle();

        let conversation = existing;

        // 2. Create if not found
        if (!conversation) {
            const { data: created, error } = await supabase
                .from("conversations")
                .insert({ user1_id: user.id, user2_id: person.id })
                .select(`
                    *,
                    user1:profiles!conversations_user1_id_fkey(id, username, full_name, avatar_url),
                    user2:profiles!conversations_user2_id_fkey(id, username, full_name, avatar_url)
                `)
                .single();

            if (error) {
                console.error("Create conversation error:", error);
                setStarting(false);
                return;
            }
            conversation = created;
        }

        // 3. Attach otherUser so ChatPanel can display the correct user info
        const withOtherUser = {
            ...conversation,
            otherUser: conversation.user1_id === user.id
                ? conversation.user2
                : conversation.user1,
        };

        setStarting(false);
        setSearch("");
        onClose();

        // 4. Tell parent to select this conversation immediately
        onConversationCreated?.(withOtherUser);
    };

    if (!open) return null;

    const displayList = search.trim() ? searchResults : suggested;
    const showEmpty = search.trim() && !searchLoading && searchResults.length === 0;

    return (
        <div className="nm-overlay" onClick={onClose}>
            {/* Stop clicks inside modal from closing it */}
            <div className="nm-modal" onClick={e => e.stopPropagation()}>

                {/* HEADER */}
                <div className="nm-header">
                    <h3>New Message</h3>
                    <button className="nm-close" onClick={onClose}>✕</button>
                </div>

                <div className="nm-divider" />

                {/* SEARCH ROW */}
                <div className="nm-search-row">
                    <span className="nm-to">To:</span>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="nm-search-input"
                    />
                    {search && (
                        <button
                            className="nm-clear"
                            onClick={() => setSearch("")}
                        >
                            ✕
                        </button>
                    )}
                </div>

                <div className="nm-divider" />

                {/* USER LIST */}
                <div className="nm-users">

                    {/* Section label */}
                    {!search.trim() && suggested.length > 0 && (
                        <div className="nm-suggested">Suggested</div>
                    )}

                    {/* Searching spinner */}
                    {searchLoading && (
                        <div className="nm-spinner-wrap">
                            <div className="nm-spinner" />
                        </div>
                    )}

                    {/* Opening chat spinner */}
                    {starting && (
                        <div className="nm-spinner-wrap">
                            <div className="nm-spinner" />
                            <span className="nm-starting-text">Opening chat…</span>
                        </div>
                    )}

                    {/* Empty state */}
                    {showEmpty && (
                        <div className="nm-no-result">No results for "{search}"</div>
                    )}

                    {/* No following yet */}
                    {!search.trim() && !searchLoading && suggested.length === 0 && (
                        <div className="nm-no-result">Follow people to message them</div>
                    )}

                    {/* Users */}
                    {!starting && displayList.map(person => (
                        <div
                            key={person.id}
                            className="nm-user-item"
                            onClick={() => handleSelectUser(person)}
                        >
                            {person.avatar_url ? (
                                <img
                                    src={person.avatar_url}
                                    alt=""
                                    className="nm-avatar"
                                />
                            ) : (
                                <div className="nm-avatar nm-avatar-fallback">
                                    {person.username?.charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div className="nm-user-text">
                                <div className="nm-username">{person.username}</div>
                                {person.full_name && (
                                    <div className="nm-fullname">{person.full_name}</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

            </div>
        </div>
    );
}

export default NewMessageModal;
